"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Icon } from "@astryxdesign/core/Icon";
import { researchIcons } from "@/lib/icons/research-icons";

export type AppToastTone = "success" | "error" | "info";

type ToastItem = {
  id: string;
  title: string;
  tone: AppToastTone;
};

type ToastApi = {
  toast: (title: string, tone?: AppToastTone) => void;
  success: (title: string) => void;
  error: (title: string) => void;
  info: (title: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const AUTO_DISMISS_MS = 4200;

function toneClasses(tone: AppToastTone): string {
  switch (tone) {
    case "success":
      return "border-emerald-500/30 bg-[var(--color-background-surface,#fff)] text-primary shadow-[0_8px_28px_rgba(0,0,0,0.14)] dark:border-emerald-400/25 dark:bg-[#1e2420] dark:shadow-[0_10px_32px_rgba(0,0,0,0.45)]";
    case "error":
      return "border-red-500/30 bg-[var(--color-background-surface,#fff)] text-primary shadow-[0_8px_28px_rgba(0,0,0,0.14)] dark:border-red-400/30 dark:bg-[#2a1f1f] dark:shadow-[0_10px_32px_rgba(0,0,0,0.45)]";
    default:
      return "border-border bg-[var(--color-background-surface,#fff)] text-primary shadow-[0_8px_28px_rgba(0,0,0,0.14)] dark:bg-[#222] dark:shadow-[0_10px_32px_rgba(0,0,0,0.45)]";
  }
}

function toneIcon(tone: AppToastTone) {
  switch (tone) {
    case "success":
      return researchIcons.checkCircle;
    case "error":
      return researchIcons.xCircle;
    default:
      return researchIcons.alertCircle;
  }
}

function toneIconClass(tone: AppToastTone): string {
  switch (tone) {
    case "success":
      return "text-emerald-600 dark:text-emerald-400";
    case "error":
      return "text-red-600 dark:text-red-400";
    default:
      return "text-secondary";
  }
}

export function AppToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const push = useCallback(
    (title: string, tone: AppToastTone = "info") => {
      const text = title.trim();
      if (!text) return;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setItems((prev) => [...prev.slice(-4), { id, title: text, tone }]);
      window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      toast: push,
      success: (title) => push(title, "success"),
      error: (title) => push(title, "error"),
      info: (title) => push(title, "info"),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {mounted
        ? createPortal(
            <div
              className="pointer-events-none fixed right-3 top-3 z-[10050] flex w-[min(100%-1.5rem,22rem)] flex-col gap-2 sm:right-4 sm:top-4"
              aria-live="polite"
              aria-relevant="additions"
            >
              {items.map((item) => (
                <div
                  key={item.id}
                  role="status"
                  className={`pointer-events-auto flex items-start gap-2.5 rounded-xl border px-3.5 py-3 transition ${toneClasses(item.tone)}`}
                >
                  <span className={`mt-0.5 shrink-0 ${toneIconClass(item.tone)}`}>
                    <Icon icon={toneIcon(item.tone)} size="sm" />
                  </span>
                  <p className="min-w-0 flex-1 text-[13px] font-medium leading-snug">
                    {item.title}
                  </p>
                  <button
                    type="button"
                    aria-label="Закрыть"
                    className="shrink-0 rounded-md p-0.5 text-secondary transition hover:bg-muted hover:text-primary"
                    onClick={() => dismiss(item.id)}
                  >
                    <Icon icon={researchIcons.close} size="sm" />
                  </button>
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}

export function useAppToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useAppToast must be used within AppToastProvider");
  }
  return ctx;
}
